import { z } from 'zod';

/**
 * Every form the app renders drives its validation off one of these
 * schemas. z.infer<> gives the input type — a single source of truth
 * so the type + the runtime check never drift.
 */

export const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const signupSchema = z.object({
  organization_name: z.string().min(1, 'Organization name is required').max(120),
  name: z.string().max(120).optional().or(z.literal('')),
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password is too long'),
});
export type SignupInput = z.infer<typeof signupSchema>;

export const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(128, 'Password is too long'),
    confirm_password: z.string().min(1, 'Confirm your password'),
  })
  .refine((v) => v.password === v.confirm_password, {
    path: ['confirm_password'],
    message: 'Passwords must match',
  });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const acceptInviteSchema = z.object({
  name: z.string().max(120).optional().or(z.literal('')),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password is too long'),
});
export type AcceptInviteInput = z.infer<typeof acceptInviteSchema>;

export const verifyEmailSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'The code is 6 digits'),
});
export type VerifyEmailInput = z.infer<typeof verifyEmailSchema>;

export const connectSmtpSchema = z.object({
  email: z.string().min(1, 'Email is required').email().toLowerCase(),
  password: z.string().min(1, 'Password / App Password is required').max(256),
  display_name: z.string().max(120).optional().or(z.literal('')),
  smtp_host: z.string().max(255).optional().or(z.literal('')),
  smtp_port: z.coerce.number().int().min(1).max(65535).optional().or(z.literal(NaN)),
  imap_host: z.string().max(255).optional().or(z.literal('')),
  imap_port: z.coerce.number().int().min(1).max(65535).optional().or(z.literal(NaN)),
});
export type ConnectSmtpInput = z.infer<typeof connectSmtpSchema>;

export const newLeadListSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120),
  description: z.string().max(500).optional().or(z.literal('')),
});
export type NewLeadListInput = z.infer<typeof newLeadListSchema>;

export const suppressionSchema = z.object({
  email: z.string().email().toLowerCase(),
  reason: z.enum(['bounced_hard', 'unsubscribed', 'manual', 'spam_complaint']),
  notes: z.string().max(500).optional().or(z.literal('')),
});
export type SuppressionInput = z.infer<typeof suppressionSchema>;
